//path: dashboard/src/pages/OperationsAssociate/AssetManagementPage.jsx

import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { 
  Plus, Upload, RefreshCw, Database, 
  Activity, Shield, Box, Search, Filter as FilterIcon,
  Loader2, Archive, Package, CheckCircle, Info
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// Components
import MasterSideBar from "../../components/MasterSideBar";
import TopBar from "../../components/TopbarNav";
import AssetsTable from "../../components/operationsAssociate/AssetsTable";
import AssetsTabs from "../../components/operationsAssociate/AssetsTabs";
import PremiumHeader from "../../components/common/PremiumHeader";
import PremiumStatCard from "../../components/common/PremiumStatCard";
import PremiumCard from "../../components/common/PremiumCard";

// Modals
import AddAssetModal from "../../components/operationsAssociate/AddAssetModal";
import EditAssetModal from "../../components/operationsAssociate/EditAssetModal";
import BulkAssetUploadModal from "../../components/operationsAssociate/BulkAssetUploadModal";
import ViewAssetModal from "../../components/operationsAssociate/ViewAssetModal";

const API_URL = "https://ims.holdthechild.net";

const AssetManagementPage = () => {
    // --- UI State ---
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    
    // --- Data State ---
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    // --- Modal States ---
    const [modal, setModal] = useState({ add: false, edit: false, view: false, bulk: false });
    const [selectedAsset, setSelectedAsset] = useState(null);

    const fetchAssets = async (isRefresh = false) => {
        isRefresh ? setRefreshing(true) : setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(`${API_URL}/api/assets`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setAssets(response.data.data || []);
        } catch (err) {
            setError("Asset registry offline.");
            toast.error("Registry Sync Failed ❌");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => { fetchAssets(); }, []);

    // Helper to toggle modals
    const toggleModal = (type, state, asset = null) => {
        setSelectedAsset(asset);
        setModal(prev => ({ ...prev, [type]: state }));
        if (!state && type !== 'view') fetchAssets(true);
    };

    return (
        <div className="flex min-h-screen bg-gray-50 font-sans overflow-hidden">
            <ToastContainer theme="colored" />
            <MasterSideBar 
                visible={sidebarOpen} 
                onClose={() => setSidebarOpen(false)} 
                collapsed={sidebarCollapsed} 
                setCollapsed={setSidebarCollapsed} 
            />

            <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0 md:ml-64'}`}>
                <TopBar onSidebarToggle={() => setSidebarOpen(true)} />

                <main className="flex-1 overflow-y-auto p-4 md:p-8 mt-16 lg:mt-20">
                    <div className="max-w-7xl mx-auto space-y-10">
                        
                        {/* Tab Navigation Hub */}
                        <div className="flex flex-col items-center">
                            <div className="bg-white p-2 rounded-[2rem] shadow-sm border border-gray-100 inline-block mb-8">
                                <AssetsTabs />
                            </div>
                            
                            <PremiumHeader 
                                title="Asset" 
                                lastWordAccent="Inventory" 
                                subtitle="Centralized registry for tracking, managing, and maintaining all organizational equipment and technology nodes." 
                            />
                        </div>

                        {/* Action Bar */}
                        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                            <div className="flex gap-4 w-full md:w-auto order-2 md:order-1">
                                <button
                                    onClick={() => toggleModal('bulk', true)}
                                    className="flex-1 md:flex-none border-2 border-gray-900 bg-white text-gray-900 px-8 py-4 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-gray-900 hover:text-white transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2"
                                >
                                    <Upload size={16}/> Bulk Load
                                </button>
                                <button
                                    onClick={() => toggleModal('add', true)}
                                    className="flex-1 md:flex-none bg-orange-600 text-white px-8 py-4 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-orange-700 transition-all transform hover:-translate-y-1 shadow-xl shadow-orange-100 flex items-center justify-center gap-2"
                                >
                                    <Plus size={18}/> New Registry Entry
                                </button>
                            </div>
                            
                            <div className="flex items-center gap-3 bg-gray-100/50 p-2 rounded-2xl order-1 md:order-2">
                                <div className="p-2 bg-white rounded-xl shadow-sm">
                                    <Activity className="text-gray-400" size={14} />
                                </div>
                                <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest mr-2">System Status: Active</span>
                            </div>
                        </div>

                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-32 bg-white rounded-[3rem] border-2 border-dashed border-gray-100">
                                <Loader2 className="animate-spin text-orange-600 mb-6" size={48} />
                                <p className="text-[11px] font-black uppercase tracking-[0.4em] text-gray-400">Synchronizing Global Asset Ledger...</p>
                            </div>
                        ) : error ? (
                            <div className="p-16 text-center bg-red-50 rounded-[3rem] border border-red-100">
                                <div className="p-6 bg-red-100/50 rounded-full w-fit mx-auto mb-6">
                                    <Info className="text-red-600" size={32} />
                                </div>
                                <h3 className="text-xl font-black text-red-900 uppercase tracking-tight mb-2">Sync Error</h3>
                                <p className="text-red-600/70 font-medium mb-8 max-w-md mx-auto">{error}</p>
                                <button onClick={() => fetchAssets()} className="bg-red-600 text-white px-10 py-4 rounded-2xl text-[11px] font-black uppercase tracking-widest flex items-center gap-3 mx-auto hover:bg-red-700 transition-transform active:scale-95 shadow-xl shadow-red-100">
                                    <RefreshCw size={16} /> Force Reconnect
                                </button>
                            </div>
                        ) : (
                            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="space-y-10">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                    <PremiumStatCard 
                                        title="Total Assets" 
                                        value={assets.length} 
                                        icon={<Database className="text-blue-600" />} 
                                        iconBg="bg-blue-50"
                                        description="Master record count"
                                    />
                                    <PremiumStatCard 
                                        title="Active Nodes" 
                                        value={assets.filter(a => a.status === 'Active').length} 
                                        icon={<Activity className="text-emerald-600" />} 
                                        iconBg="bg-emerald-50"
                                        description="Operational equipment"
                                    />
                                    <PremiumStatCard 
                                        title="In Maintenance" 
                                        value={assets.filter(a => a.status === 'Under Repair').length} 
                                        icon={<Shield className="text-orange-600" />} 
                                        iconBg="bg-orange-50"
                                        description="Safety & repair cycle"
                                    />
                                    <PremiumStatCard 
                                        title="Registry Health" 
                                        value="98.2%" 
                                        icon={<Box className="text-purple-600" />} 
                                        iconBg="bg-purple-50"
                                        description="Data Integrity Index"
                                    />
                                </div>
                                
                                {refreshing && (
                                    <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="flex items-center gap-3 px-6 py-3 bg-orange-50 text-orange-600 rounded-full w-fit mx-auto shadow-sm border border-orange-100">
                                        <RefreshCw className="animate-spin" size={14} />
                                        <span className="text-[10px] font-black uppercase tracking-widest">Optimizing Ledger Balance...</span>
                                    </motion.div>
                                )}

                                <PremiumCard icon={<Archive size={18} />} title="Master Asset Directory">
                                    <div className="p-2 overflow-hidden">
                                        <AssetsTable
                                            assets={assets}
                                            onEdit={(a) => toggleModal('edit', true, a)}
                                            onView={(a) => toggleModal('view', true, a)}
                                            onAssetUpdate={() => fetchAssets(true)}
                                        />
                                    </div>
                                </PremiumCard>
                            </motion.div>
                        )}
                    </div>
                </main>
            </div>

            {/* --- Modals Overlay System --- */}
            <AnimatePresence>
                {(modal.add || modal.edit || modal.view || modal.bulk) && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                        <motion.div 
                            initial={{ opacity: 0 }} 
                            animate={{ opacity: 1 }} 
                            exit={{ opacity: 0 }}
                            onClick={() => {
                                if (modal.view) toggleModal('view', false);
                            }}
                            className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" 
                        />
                        <motion.div 
                            initial={{ scale: 0.9, opacity: 0, y: 20 }} 
                            animate={{ scale: 1, opacity: 1, y: 0 }} 
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            className="relative bg-white rounded-[2.5rem] shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto border border-gray-100"
                        >
                            {modal.add && <AddAssetModal closeModal={() => toggleModal('add', false)} onAssetAdded={() => fetchAssets(true)} />}
                            {modal.edit && <EditAssetModal assetId={selectedAsset?._id} closeModal={() => toggleModal('edit', false)} onAssetUpdated={() => fetchAssets(true)} />}
                            {modal.bulk && <BulkAssetUploadModal closeModal={() => toggleModal('bulk', false)} onAssetsUploaded={() => fetchAssets(true)} />}
                            {modal.view && <ViewAssetModal asset={selectedAsset} closeModal={() => toggleModal('view', false)} />}
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AssetManagementPage;